import React, { useState, useEffect } from 'react';
// import axios from 'axios';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { styled } from '@uhg-abyss/web/tools/styled';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Tabs } from '@uhg-abyss/web/ui/Tabs';
import { CodeSnippet } from './CodeSnippet';
import { ResponseV3 } from './ResponseV3';
import { ParameterTable } from './ParameterTable';
import { BodyV3 } from './BodyV3';
import { LagoonContext } from '@src/AppContext';

const ResponseHeading = styled('h4', {
  marginBottom: '8px',
  fontSize: '18px !important',
  fontWeight: 400,
});

const SandBoxContainer = styled('div', {
  position: 'relative',
  backgroundColor: '#FFFFFF',
  '.response-heading': {
    fontSize: '20px',
  },
});

const ActionContainer = styled('div', {
  zIndex: 2,
  '@screen >= $xl': {
    position: 'absolute',
    right: 0,
    marginTop: 8,
  },
});

const CallContainer = styled('div', {
  display: 'flex',
  position: 'relative',
  zIndex: 2,
});

const CallType = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #D0D5DD',
  backgroundColor: '#FFFFFF',
  borderRadius: 4,
  padding: '0 8px',
  fontWeight: 700,
  height: 38,
  minWidth: 80,
  textTransform: 'uppercase',
  variants: {
    variant: {
      get: {
        color: '#087443',
        borderColor: '#087443',
        backgroundColor: '#E8F7EE',
      },
      post: {
        color: '#005EB8',
        borderColor: '#005EB8',
        backgroundColor: '#E9F2FF',
      },
      put: {
        color: '#9A6200',
        borderColor: '#9A6200',
        backgroundColor: '#FFF5E6',
      },
      delete: {
        color: '#B42318',
        borderColor: '#B42318',
        backgroundColor: '#FEECEC',
      },
    },
  },
});

const UrlInputContainer = styled('div', {
  flexGrow: 1,
  margin: '0 8px',
});

export const ServiceSandboxV3 = ({ swagger, currentApi, service }) => {
  const lagoonContext = React.useContext(LagoonContext);
  const { method, path } = currentApi;
  const normalizedMethod =
    typeof method === 'string' ? method.trim().toLowerCase() : 'get';
  const { servers } = swagger;
  const [queryParams, setQueryParams] = useState([]);
  const [headerParams, setHeaderParams] = useState([]);
  const [pathParams, setPathParams] = useState([]);
  const [formDataParams, setFormDataParams] = useState([]);
  const [responseOptions, setResponseOptions] = useState([]);
  const [requestMap, setRequestMap] = useState({});
  const [serviceResponse, setServiceResponse] = useState(null);
  const [selectedExample, setSelectedExample] = useState(null);
  const [isCodeOpen, openCodeModal] = useState(false);

  const getPathUrl = () => {
    if (!servers || !servers[0] || !path) return '';
    return `${servers[0].url}${path}`;
  };

  const url = getPathUrl();

  const form = useForm({
    defaultValues: { url, response: responseOptions[0] || '200 - OK' },
  });

  const getQueryAndHeaderParams = () => {
    if (!currentApi) return;
    const query = [];
    const header = [];
    const pathParams = [];
    const formData = [];

    if (!currentApi.parameters) {
      const envHeader = {
        key: 'env',
        value: 'sandbox',
        type: 'string',
        description: 'env',
        required: false,
      };
      setQueryParams([]);
      setHeaderParams([envHeader]);
      setPathParams([]);
      setFormDataParams([]);
      return;
    }

    currentApi.parameters.forEach((param) => {
      const data = {
        key: param.name,
        value: param.schema.example,
        type: param.schema ? param.schema.type : 'string',
        format: param.schema ? param.schema.format : '',
        description: param.description || '',
        required: param.required || false,
      };
      if (param.in === 'query') {
        query.push(data);
      } else if (param.in === 'header') {
        header.push(data);
      } else if (param.in === 'path') {
        pathParams.push(data);
      } else if (param.in === 'formData') {
        formData.push(data);
      }
    });

    const envHeader = {
      key: 'env',
      value: 'sandbox',
      type: 'string',
      description: 'env',
      required: false,
    };

    header.push(envHeader);

    setQueryParams(query);
    setHeaderParams(header);
    setPathParams(pathParams);
    setFormDataParams(formData);
  };

  useEffect(() => {
    if (!requestMap) return;

    const selectedValue = form.getValues('response');
    const contentType = requestMap[selectedValue]?.type;
    const schema = requestMap[selectedValue]?.schema;

    const exampleValues = requestMap[selectedValue]?.value || {};

    if (contentType) {
      const contentTypeHeaderIndex = headerParams.findIndex((header) => {
        return header.key.toLowerCase() === 'content-type';
      });

      const updatedHeaders = [...headerParams];
      const contentTypeHeader = {
        key: 'Content-Type',
        value: contentType,
        type: 'string',
        description: 'The media type of the request body',
        required: true,
      };

      if (contentTypeHeaderIndex >= 0) {
        updatedHeaders[contentTypeHeaderIndex] = contentTypeHeader;
      } else {
        updatedHeaders.push(contentTypeHeader);
      }

      setHeaderParams(updatedHeaders);

      if (contentType.includes('multipart/form-data')) {
        const formFields = [];

        if (schema && schema.properties) {
          Object.entries(schema.properties).forEach(
            ([propName, propSchema]) => {
              const exampleValue = exampleValues[propName];

              let valueToUse = '';
              let typeToUse = propSchema.type || 'string';

              if (exampleValue !== undefined) {
                if (typeof exampleValue === 'object') {
                  valueToUse = JSON.stringify(exampleValue);
                  typeToUse = 'object';
                } else {
                  valueToUse = exampleValue;
                }
              } else if (propSchema.example !== undefined) {
                valueToUse =
                  propSchema.type === 'object'
                    ? JSON.stringify(propSchema.example)
                    : propSchema.example;
              }

              typeToUse = propSchema.format === 'binary' ? 'file' : typeToUse;

              formFields.push({
                key: propName,
                format: propSchema.format || '',
                value: valueToUse,
                type: typeToUse,
                in: 'formData',
                description:
                  propSchema.description || `Form field: ${propName}`,
                required: schema.required?.includes(propName) || false,
              });
            }
          );
        } else if (Object.keys(exampleValues).length > 0) {
          Object.entries(exampleValues).forEach(([key, value]) => {
            formFields.push({
              key,
              format: '',
              value: typeof value === 'object' ? JSON.stringify(value) : value,
              type: typeof value === 'object' ? 'object' : typeof value,
              description: `Form field: ${key}`,
              required: false,
            });
          });
        }

        setSelectedExample(null);
        setFormDataParams(formFields);
      } else if (contentType.includes('application/json')) {
        if (formDataParams.length > 0) {
          const jsonBody = formDataParams.reduce((obj, param) => {
            let value = param.value;
            if (param.type === 'object' && typeof value === 'string') {
              try {
                value = JSON.parse(value);
              } catch {
                // Keep as string if parsing fails
              }
            }
            obj[param.key] = value;
            return obj;
          }, {});

          setFormDataParams([]);
          setSelectedExample(jsonBody);
        } else {
          setSelectedExample(exampleValues);
        }
      }
    }
  }, [requestMap, form.getValues('response')]);

  useEffect(() => {
    setServiceResponse(null);
    setRequestMap({});
    setSelectedExample(null);
    getAllSampleRequests();
  }, [currentApi]);

  const getAllSampleRequests = () => {
    const reqMap = {};
    const responseOpts = [];
    const isSuperconnector = service === 'superconnector';

    // Services that show all response options in the dropdown
    const SHOW_ALL_RESPONSES =
      lagoonContext?.getTable('configs').getRow('SHOW_ALL_RESPONSES')?.value ||
      [];

    // Added this code for provideraccess to show all response options in the dropdown and map them to request body examples
    if (service === 'provideraccess') {
      const getResponseLabel = (resp) => {
        return `${resp.status} - ${resp.description || 'Response'}`;
      };
      const addResponseOption = (label) => {
        responseOpts.push({ label, value: label });
      };

      const responses = currentApi?.responses || [];
      const showAll = SHOW_ALL_RESPONSES.includes(service);

      if (showAll) {
        responses.forEach((resp) => {
          addResponseOption(getResponseLabel(resp));
        });
      } else if (responses.length > 0) {
        addResponseOption(getResponseLabel(responses[0]));
      }

      if (responseOpts.length === 0) {
        addResponseOption('200 - OK');
      }

      const requestBodyContent = currentApi?.requestBody?.content;
      if (!requestBodyContent) {
        setResponseOptions(responseOpts);
        setRequestMap(reqMap);
        return;
      }

      const requestSamples = [];

      Object.entries(requestBodyContent).forEach(([contentType, content]) => {
        const schema = content?.schema;
        const examplesList = content?.examples || {};
        const directExample = content?.example;

        if (Object.keys(examplesList).length > 0) {
          Object.values(examplesList).forEach((example) => {
            requestSamples.push({
              type: contentType,
              value: example.value || {},
              schema,
            });
          });
          return;
        }

        if (directExample) {
          requestSamples.push({
            type: contentType,
            value: directExample,
            schema,
          });
          return;
        }

        if (schema?.example) {
          requestSamples.push({
            type: contentType,
            value: schema.example,
            schema,
          });
          return;
        }

        if (schema) {
          requestSamples.push({
            type: contentType,
            value: {},
            schema,
          });
        }
      });

      responseOpts.forEach(({ value }, index) => {
        const requestData = requestSamples[index] || requestSamples[0];

        if (requestData) {
          reqMap[value] = requestData;
        }
      });

      setResponseOptions(responseOpts);
      setRequestMap(reqMap);
      return;
    }

    // For non-superconnector: build dropdown from response status codes
    if (!isSuperconnector) {
      const responses = currentApi?.responses || [];
      const showAll = SHOW_ALL_RESPONSES.includes(service);

      if (showAll) {
        responses.forEach((resp) => {
          const label = `${resp.status} - ${resp.description || 'Response'}`;
          responseOpts.push({ label, value: label });
        });
      } else if (responses.length > 0) {
        const first = responses[0];
        const label = `${first.status} - ${first.description || 'Response'}`;
        responseOpts.push({ label, value: label });
      }

      if (responseOpts.length === 0) {
        responseOpts.push({ label: '200 - OK', value: '200 - OK' });
      }

      // Extract request body example for the Body tab
      const { requestBody } = currentApi || {};
      if (requestBody?.content) {
        const contentType = Object.keys(requestBody.content)[0];
        if (contentType) {
          const content = requestBody.content[contentType];
          const examplesList = content?.examples || {};
          const firstExampleKey = Object.keys(examplesList)[0];

          if (firstExampleKey) {
            setSelectedExample(examplesList[firstExampleKey].value || {});
          } else if (content?.example) {
            setSelectedExample(content.example);
          } else if (content?.schema?.example) {
            setSelectedExample(content.schema.example);
          }
        }
      }

      setResponseOptions(responseOpts);
      setRequestMap(reqMap);
      return;
    }

    // Superconnector: keep existing behavior — build from requestBody examples
    const { requestBody } = currentApi || {};
    if (!requestBody || !requestBody.content) {
      setResponseOptions(responseOpts);
      setRequestMap(reqMap);
      return;
    }

    Object.keys(requestBody.content).forEach((contentType) => {
      const content = requestBody.content[contentType];
      const schema = content?.schema;
      const examplesList = content?.examples || {};
      const directExample = content?.example;

      if (Object.keys(examplesList).length > 0) {
        Object.keys(examplesList).forEach((key) => {
          const example = examplesList[key];
          const requestData = {
            type: contentType,
            value: example.value || {},
            schema: schema,
          };
          responseOpts.push({ label: key, value: key });
          reqMap[key] = requestData;
        });
      } else if (directExample) {
        const label = '200 - OK';
        responseOpts.push({ label, value: label });
        reqMap[label] = { type: contentType, value: directExample, schema };
      } else if (schema?.example) {
        const label = '200 - OK';
        responseOpts.push({ label, value: label });
        reqMap[label] = { type: contentType, value: schema.example, schema };
      }
    });

    setResponseOptions(responseOpts);
    setRequestMap(reqMap);
  };

  useEffect(() => {
    if (form.getValues('url') !== url) {
      form.setValue('url', url);
    }

    getQueryAndHeaderParams();
    getAllSampleRequests();
  }, [url]);

  useEffect(() => {
    if (responseOptions.length > 0) {
      const defaultOption = responseOptions[0].value;
      form.setValue('response', defaultOption);

      if (requestMap && requestMap[defaultOption]) {
        setSelectedExample(requestMap[defaultOption].value);
      }
    }
  }, [responseOptions, requestMap]);

  const getCurrentParameters = () => {
    const currentQueryParams = queryParams.map((param) => {
      return {
        ...param,
        value: form.getValues(param.key) || param.value,
      };
    });

    const currentHeaderParams = headerParams.map((param) => {
      return {
        ...param,
        value: form.getValues(param.key) || param.value,
      };
    });

    const currentPathParams = pathParams.map((param) => {
      return {
        ...param,
        value: form.getValues(param.key) || param.value,
      };
    });

    const currentFormDataParams = formDataParams.map((param) => {
      if (param.type === 'file') {
        return {
          ...param,
          value: form.getValues(`${param.key}_file`) || param.value,
        };
      }
      return {
        ...param,
        value: form.getValues(param.key) || param.value,
      };
    });

    return {
      queryParams: currentQueryParams,
      headerParams: currentHeaderParams,
      pathParams: currentPathParams,
      formDataParams: currentFormDataParams,
    };
  };

  const handleSendRequest = () => {
    const selectedResponseValue = form.getValues('response');

    if (currentApi && currentApi.responses) {
      let responseData = null;
      const isSuperconnector = service === 'superconnector';

      Object.entries(currentApi.responses).forEach(([index, responseObj]) => {
        const responseLabel = `${responseObj.status} - ${
          responseObj.description || 'Response'
        }`;
        if (
          (!isSuperconnector && responseLabel === selectedResponseValue) ||
          (isSuperconnector && responseObj.status === '200')
        ) {
          responseData = responseObj;
        }
      });

      if (responseData) {
        const { content } = responseData;
        const respContentTypes = content ? Object.keys(content) : [];

        if (respContentTypes.length === 0) {
          setServiceResponse({
            data: 'No sample responses are defined for this API',
          });
          return;
        }

        // Extract example from the matched response
        let exampleData = null;

        if (isSuperconnector) {
          // Superconnector: look up response example by selected example name
          respContentTypes.forEach((type) => {
            if (exampleData) return;
            if (content[type].examples?.[selectedResponseValue]) {
              exampleData = content[type].examples[selectedResponseValue].value;
            }
          });
          // Fallback to first available example if no name match
          if (!exampleData) {
            respContentTypes.forEach((type) => {
              if (exampleData) return;
              if (content[type].example) {
                exampleData = content[type].example;
              } else if (content[type].examples) {
                const firstKey = Object.keys(content[type].examples)[0];
                if (firstKey) {
                  exampleData = content[type].examples[firstKey].value;
                }
              } else if (content[type].schema?.example) {
                exampleData = content[type].schema.example;
              }
            });
          }
        } else {
          // Non-superconnector: take the first available example
          respContentTypes.forEach((type) => {
            if (exampleData) return;
            if (content[type].example) {
              exampleData = content[type].example;
            } else if (content[type].examples) {
              const firstKey = Object.keys(content[type].examples)[0];
              if (firstKey) {
                exampleData = content[type].examples[firstKey].value;
              }
            } else if (content[type].schema?.example) {
              exampleData = content[type].schema.example;
            }
          });
        }

        setServiceResponse({
          data:
            exampleData || 'No sample response found for the selected option',
        });
      } else {
        setServiceResponse({
          data: 'No sample response found for the selected option',
        });
      }
    } else {
      setServiceResponse({
        data: 'No sample responses are defined for this API',
      });
    }
  };

  return (
    <React.Fragment>
      <SandBoxContainer>
        <FormProvider state={form}>
          <CallContainer>
            <CallType variant={normalizedMethod} css={{ fontSize: '15px' }}>
              {method}
            </CallType>
            <UrlInputContainer>
              <TextInput
                css={{ 'abyss-text-input': { fontSize: '14px !important' } }}
                label="API URL"
                hideLabel
                width="auto"
                isReadOnly
                model="url"
              ></TextInput>
            </UrlInputContainer>
            <Button
              type="submit"
              fontSize="15px"
              css={{
                width: 150,
                borderRadius: 4,
                fontFamily: 'AbyssEnterpriseSansVF, Arial, sans-serif',
                fontWeight: 'bold',
                fontSize: '15px !important',
              }}
              size="38px"
              onClick={handleSendRequest}
            >
              Send Request
            </Button>
          </CallContainer>
          <ActionContainer>
            <Layout.Group alignItems="bottom">
              <SelectInput
                css={{
                  'abyss-select-input-menu': {
                    zIndex: '9999 !important',
                    position: 'absolute !important',
                    fontSize: '14px !important',
                  },
                  'abyss-select-input-container': {
                    fontSize: '14px !important',
                  },
                  'abyss-select-input-menu-option-label': {
                    fontSize: '14px !important',
                  },
                }}
                label={
                  <span
                    style={{
                      fontSize: '15px',
                      color: '#323334',
                      fontWeight: 'bold',
                    }}
                  >
                    Sample Responses
                  </span>
                }
                placeholder="200 - OK"
                onChange={(e) => {
                  setServiceResponse(null);
                  const selectedValue = e;
                  const isSuperconnector = service === 'superconnector';

                  if (!isSuperconnector) {
                    return;
                  }

                  const requestData = requestMap[selectedValue];

                  if (requestData) {
                    const contentType = requestData.type;
                    const newExampleValue = requestData.value;

                    if (contentType.includes('multipart/form-data')) {
                      setSelectedExample(null);

                      if (requestData.schema && requestData.schema.properties) {
                        const formFields = Object.entries(
                          requestData.schema.properties
                        ).map(([propName, propSchema]) => {
                          const exampleValue = newExampleValue[propName];

                          let valueToUse = '';
                          let typeToUse = propSchema.type || 'string';

                          if (exampleValue !== undefined) {
                            valueToUse =
                              typeof exampleValue === 'object'
                                ? JSON.stringify(exampleValue)
                                : exampleValue;
                            typeToUse =
                              typeof exampleValue === 'object'
                                ? 'object'
                                : typeof exampleValue;
                          } else if (propSchema.example !== undefined) {
                            valueToUse =
                              propSchema.type === 'object'
                                ? JSON.stringify(propSchema.example)
                                : propSchema.example;
                          }

                          typeToUse =
                            propSchema.format === 'binary' ? 'file' : typeToUse;

                          return {
                            key: propName,
                            format: propSchema.format || '',
                            value: valueToUse,
                            type: typeToUse,
                            description:
                              propSchema.description ||
                              `Form field: ${propName}`,
                            required:
                              requestData.schema.required?.includes(propName) ||
                              false,
                          };
                        });

                        setFormDataParams(formFields);
                      } else {
                        const formFields = Object.entries(
                          newExampleValue || {}
                        ).map(([key, value]) => {
                          return {
                            key,
                            format: '',
                            value:
                              typeof value === 'object'
                                ? JSON.stringify(value)
                                : String(value),
                            type:
                              typeof value === 'object'
                                ? 'object'
                                : typeof value,
                            description: `Form field: ${key}`,
                            required: false,
                          };
                        });

                        setFormDataParams(formFields);
                      }
                    } else {
                      setFormDataParams([]);
                      setSelectedExample(newExampleValue);
                    }
                  }
                }}
                width="250px"
                options={responseOptions}
                model="response"
              />
              <Button
                css={{
                  width: 150,
                  borderRadius: 4,
                  'abyss-button-content-container': {
                    height: '38px',
                  },
                  fontFamily: 'AbyssEnterpriseSansVF, Arial, sans-serif',
                  fontWeight: 'bold',
                  fontSize: '15px !important',
                }}
                size="32px"
                fontSize="15px"
                variant="outline"
                onClick={() => {
                  const currentParams = getCurrentParameters();

                  openCodeModal(true);

                  setQueryParams(currentParams.queryParams);
                  setHeaderParams(currentParams.headerParams);
                  setPathParams(currentParams.pathParams);
                  setFormDataParams(currentParams.formDataParams);
                }}
              >
                Code Snippet
              </Button>
            </Layout.Group>
          </ActionContainer>
          <Tabs
            title="Request Parameters"
            size="$sm"
            variant="line"
            grow={false}
            css={{
              'abyss-tabs-list': {
                border: 'none',
                minHeight: 40,
              },
              'abyss-tabs-content-container': {
                border: 'none',
                paddingLeft: 0,
                paddingRight: 0,
                marginTop: '16px',
              },
              'abyss-tabs-extended-border': {
                display: 'none',
              },
              '.abyss-tabs-tab': {
                background: 'none',
              },
            }}
          >
            {queryParams.length > 0 && (
              <Tabs.Tab label="Query">
                <ParameterTable
                  title="Query Parameters"
                  parameters={queryParams}
                  setValue={form.setValue}
                />
              </Tabs.Tab>
            )}
            {headerParams.length > 0 && (
              <Tabs.Tab label="Headers">
                <ParameterTable
                  title="Headers"
                  parameters={headerParams}
                  setValue={form.setValue}
                />
              </Tabs.Tab>
            )}
            {pathParams.length > 0 && (
              <Tabs.Tab label="Path">
                <ParameterTable
                  title="Path"
                  parameters={pathParams}
                  setValue={form.setValue}
                />
              </Tabs.Tab>
            )}
            {currentApi?.requestBody && selectedExample && (
              <Tabs.Tab label="Body">
                <BodyV3 body={selectedExample} currentApi={currentApi} />
              </Tabs.Tab>
            )}
            {formDataParams.length > 0 && (
              <Tabs.Tab label="Form Data">
                <ParameterTable
                  title="Form Data"
                  parameters={formDataParams}
                  setValue={form.setValue}
                />
              </Tabs.Tab>
            )}
          </Tabs>
          <ResponseHeading className="response-heading">
            Response:
          </ResponseHeading>
          <ResponseV3 response={serviceResponse} />
        </FormProvider>
      </SandBoxContainer>
      {isCodeOpen && (
        <ModalDialog
          size="800px"
          header="Code Snippet"
          isOpen={isCodeOpen}
          onClose={() => {
            return openCodeModal(false);
          }}
        >
          <CodeSnippet
            url={url}
            method={method}
            query={queryParams}
            headers={headerParams}
            body={selectedExample}
            formData={formDataParams}
            path={pathParams}
          />
        </ModalDialog>
      )}
    </React.Fragment>
  );
};
